import { AsyncPipe, DecimalPipe } from '@angular/common';
import { AfterContentInit, Component, ElementRef, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProductAdditions, ProductComposition, ProductLine } from 'src/app/models';
import { buttonIcons } from 'src/app/modules/icon.module';
import { MenyBasketItemComponent } from 'src/app/pages/basket/overview/meny-basket-overview/meny-basket-item/meny-basket-item.component';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { ContentService } from 'src/app/services/content/content.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { IconButtonComponent, TextButtonComponent } from 'src/app/shared/components';
import { ProductCardComponent } from 'src/app/shared/components/product/card';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';
import { DisplayCurrencyPipe } from '../../../../../../shared/pipes/display-currency.pipe';

export interface MenyBasketDialogData {
  product: ProductComposition;
  lineId: number;
  initialQuantity: number;
}

@Component({
  selector: 'app-meny-basket-dialog',
  templateUrl: './meny-basket-dialog.component.html',
  styleUrls: ['./meny-basket-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IconButtonComponent,
    MenyBasketItemComponent,
    AsyncPipe,
    DecimalPipe,
    DisplayCurrencyPipe,
    TextButtonComponent,
    ProductCardComponent,
  ],
})
export class MenyBasketDialogComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  dialogRef = inject<MatDialogRef<MenyBasketDialogComponent>>(MatDialogRef);
  data = inject<MenyBasketDialogData>(MAT_DIALOG_DATA);
  private basketQuery = inject(BasketQuery);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);

  private subscription: Subscription;

  closeIcon = buttonIcons.bigCross;
  productsUrl = `/${environment.urls.products}`;
  basketOverviewUrl = `/${environment.urls.basket}`;
  locale = environment.i18n.language;
  currency = environment.i18n.currency;

  buyMoreButtonText: string;
  basketButtonText: string;

  productLine: BehaviorSubject<ProductLine>;
  quantity: BehaviorSubject<number>;

  productAdditions: BehaviorSubject<ProductLine[]>;
  displayedProductAddition: ProductAdditions;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'basket');

    this.subscription = new Subscription();

    this.buyMoreButtonText = 'Køb mere';
    this.basketButtonText = this.content.get('goToBasketButtonText');
  }

  ngOnInit() {
    this.productLine = new BehaviorSubject(undefined);
    this.quantity = new BehaviorSubject(this.data.initialQuantity);
    this.productAdditions = new BehaviorSubject([]);

    this.subscription.add(
      this.basketQuery.selectProductLines$.subscribe((productLines) => {
        const productLine = productLines.find((line) => line.lineId === this.data.lineId);
        this.productLine.next(productLine);
        this.quantity.next(productLine?.quantity);

        // TODO: use baseCard on product-card, manage locally, cascade changes to basket
        const additions: ProductLine[] = [];
        this.productAdditions.next(additions);
      }),
    );

    this.displayedProductAddition = { ...this.data.product.productAdditions?.find((productAddition) => productAddition.display) };
    this.displayedProductAddition.products = this.displayedProductAddition?.products
      ? this.displayedProductAddition.products.filter((p) => p.forSale && !p.hasModules)
      : [];
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  redirectAndClose(url: string) {
    this.router.navigate([url]);
    this.dialogRef.close();
  }

  close() {
    this.dialogRef.close();
  }

  getPrice(productLine: ProductLine, productAdditions: ProductLine[]): number {
    let sum = 0;

    if (productLine) {
      const PARENT_QUANTITY = productLine.quantity;

      sum += productLine.price * PARENT_QUANTITY;
      if (productLine.moduleLines) {
        for (const entry of productLine.moduleLines) {
          sum += entry.price * entry.quantity * PARENT_QUANTITY;
        }
      }
    }

    for (const line of productAdditions) {
      if (!line) {
        continue;
      }

      const PARENT_QUANTITY = line.quantity;

      sum += line.price * PARENT_QUANTITY;
      if (line.moduleLines) {
        for (const entry of line.moduleLines) {
          sum += entry.price * entry.quantity * PARENT_QUANTITY;
        }
      }
    }

    return sum;
  }
}
